import { useDeleteShareTeamMember, useRemoveTeamMember } from '@hooks/deleteQueries';
import {
  useFetchShareTeamMember,
  useFetchTeamMembers,
  useProfile,
  useSingleStudy,
  useWorkspaceMembers,
} from '@hooks/getQueries';
import {
  useAddPermissionToTeam,
  useChangeStudyOwner,
  useInviteTeamMember,
} from '@hooks/postQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import useWorkspace from '@hooks/useWorkspace';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  Container,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { blue, deepOrange, deepPurple, green, pink, red } from '@mui/material/colors';
import { useState } from 'react';
import { toast } from 'react-hot-toast';

import useWorkspacePermissions from '@hooks/workspacePermissions';
import DeleteConfirmationDialog from './DeleteConfirmation';
import PublicShare from './dynamic-survey/PublicShare';
import InviteMemberDialogCollaborators from './InviteMemberDialogCollaborators';
import { BootstrapDialog } from './shared/BootstrapDialog';
import Spinner from './spinner/Spinner';
const frontendUrl = import.meta.env.VITE_FRONTEND_URL;

const getColorFromChar = (char) => {
  const charCode = char?.toLowerCase()?.charCodeAt(0);
  const colors = [
    deepOrange[500],
    deepPurple[500],
    green[500],
    pink[500],
    blue[500],
    red[500],
    [500],
  ];
  return colors[(charCode - 97) % colors.length];
};

export default function TeamMembersModal({
  type = 'survey',
  studyId,
  open,
  onOpen = () => {},
  onClose = () => {},
  showPreviewAvatars = true,
}) {
  const { data: userData } = useProfile();
  const { data: survey, isPending: isStudyLoading } = useSingleStudy(studyId, type);
  const [permission, setPermission] = useState('view');
  const [showTeamMembers, setShowTeamMembers] = useState(true);
  const [openPublicShare, setOpenPublicShare] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const studyUrlType = type === 'survey' ? 'dynamic-surveys' : 'qualitative-studies';
  const { open: dialogOpen, show, handleConfirm, handleClose } = useConfirmationDialog();
  const { mutateAsync: inviteTeamMember, isPending: isInvitePending } = useInviteTeamMember();
  const { mutateAsync: addPermission } = useAddPermissionToTeam();
  const { mutateAsync: deleteShareMember, isPending: isDeletePending } = useDeleteShareTeamMember();
  const { mutateAsync: removeTeamMember } = useRemoveTeamMember();
  const { mutateAsync: changeOwner } = useChangeStudyOwner();
  const id = localStorage.getItem('activeWorkspaceId');
  const { canManageMembers } = useWorkspacePermissions(id);

  const { activeWorkspace } = useWorkspace();
  const { data: workspaceMembers = [], refetch: refetchWorkspaceMembers } = useWorkspaceMembers(
    activeWorkspace?.id,
  );

  const { data: teamMembers = [], isPending: isTeamLoading } = useFetchTeamMembers({
    studyId,
    studyUrlType,
  });

  const isOwner = survey?.owner_id === userData?.id;
  const isAdmin = teamMembers?.find((m) => m.user_email === userData?.email)?.role === 'admin';
  const canManageTeam = isOwner || isAdmin;

  const { data: shareTeamMembers = [] } = useFetchShareTeamMember(
    {
      studyId,
      studyUrlType,
      enabled: canManageTeam,
    },
    open,
  );

  // This is the return url for the share requestπ
  const returnUrl = `${frontendUrl}/accept-share-request/${studyId}/${studyUrlType}/`;

  const [inputValue, setInputValue] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [chips, setChips] = useState([]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Check if email exists in workspace members
  const getEmailOptions = (inputValue) => {
    const existingEmails = workspaceMembers.map((member) => member.user_email).filter(Boolean);
    const filteredEmails = existingEmails.filter((email) =>
      email.toLowerCase().includes(inputValue.toLowerCase()),
    );

    const options = [...filteredEmails];

    // If input is a valid email and not in workspace, add "Add to workspace" option
    if (
      emailRegex.test(inputValue.trim()) &&
      !existingEmails.includes(inputValue.trim()) &&
      inputValue.trim() !== '' &&
      canManageMembers
    ) {
      options.push(`Add "${inputValue.trim()}" to workspace`);
    }

    return options;
  };

  const hasEditPermission = teamMembers
    ?.find((member) => member.user_email === userData.email)
    ?.permissions?.includes('edit');

  const canInvite = canManageTeam;
  const canViewTeamList = canManageTeam || hasEditPermission;

  const [dialogDescription, setDialogDescription] = useState({
    title: 'Remove Member',
    description: "This member won't be able to access this study if you remove them.",
    okText: 'Remove',
  });

  const handleEmailChange = (event, newValue) => {
    if (newValue && newValue.startsWith('Add "') && newValue.endsWith('" to workspace')) {
      // Extract email from "Add "email" to workspace" format
      const email = newValue.match(/Add "(.+)" to workspace/)?.[1];
      if (email && activeWorkspace?.id && canManageMembers) {
        setInviteEmail(email);
        setOpenInviteDialog(true);
        setInputValue('');
      }
    } else {
      setSelectedEmail(newValue);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleDeleteChip = (chipToDelete) => {
    setChips((prevChips) => prevChips.filter((chip) => chip !== chipToDelete));
  };

  const toggleShowTeamMembers = () => setShowTeamMembers(!showTeamMembers);

  const handleDeleteShareRequest = async (email) => {
    await deleteShareMember({ studyId, studyUrlType, email });
  };

  const handleRemoveMember = async (email) => {
    await removeTeamMember({ studyId, studyUrlType, email });
  };

  const handleChangeOwner = async (email) => {
    await changeOwner({ studyId, studyUrlType, email, returnUrl });
  };

  const addTeamMemberInvite = async (email) => {
    try {
      if (permission === 'owner') {
        await handleChangeOwner(email);
        setPermission('view');
        return;
      } else {
        await inviteTeamMember({
          payload: {
            role: permission === 'admin' ? 'admin' : 'member',
            permissions:
              permission === 'admin'
                ? ['view', 'edit', 'admin']
                : permission === 'edit'
                  ? ['view', 'edit']
                  : ['view'],
            user_email: email,
          },
          studyId,
          studyUrlType,
          returnUrl,
        });
      }
    } catch (error) {
      toast.error(error?.detail || 'Failed to invite member. Please try again.');
    }
  };

  const handleTeamMemberInvite = async () => {
    if (chips.length > 0) {
      for (let i = 0; i < chips.length; i++) {
        const email = chips[i];
        await addTeamMemberInvite(email);
      }
      setChips([]);
    } else {
      const emailToInvite = selectedEmail || inputValue.trim();
      if (emailRegex.test(emailToInvite)) {
        if (userData.email === emailToInvite) {
          toast.error('You cannot invite yourself');
          return;
        }
        // Check if user is in workspace
        const isInWorkspace = workspaceMembers.some(
          (member) => member.user_email === emailToInvite,
        );
        if (!isInWorkspace) {
          if (!canManageMembers) {
            toast.error('You do not have permission to add members to the workspace');
            return;
          }
          toast.error('User must be added to workspace first');
          setInviteEmail(emailToInvite);
          setOpenInviteDialog(true);
          return;
        }
        await addTeamMemberInvite(emailToInvite);
        setInputValue('');
        setSelectedEmail(null);
      }
    }
  };

  const handleInviteDialogSuccess = () => {
    refetchWorkspaceMembers();
    // After successful workspace invitation, we can attempt to add them to the study
    if (inviteEmail && emailRegex.test(inviteEmail)) {
      // Note: The user will need to accept the workspace invitation first
      // before they can be added to the study
      toast.success('User can be added to study after accepting workspace invitation.');
    }
  };

  const handleAddPermission = async (email, role, permissions) => {
    await addPermission({
      payload: {
        role,
        permissions: [...permissions],
        user_email: email,
      },
      studyId,
      studyUrlType,
      returnUrl,
    });
  };

  return (
    <>
      {/* team preview button */}
      {canViewTeamList && showPreviewAvatars && (!canManageTeam || !isTeamLoading) && (
        <Box
          onClick={onOpen}
          sx={{
            cursor: 'pointer',
            display: 'inline-block',
          }}
        >
          <AvatarGroup
            max={2}
            sx={{
              '& .MuiAvatar-root': {
                width: 36,
                height: 36,
              },
            }}
          >
            {teamMembers?.map((member, i) => {
              if (!member?.user_email) return null;
              const firstChar = member.user_email.charAt(0).toUpperCase();
              const backgroundColor = getColorFromChar(firstChar);
              return (
                <Avatar key={i} sx={{ bgcolor: backgroundColor }}>
                  {firstChar}
                </Avatar>
              );
            })}

            <Avatar sx={{ bgcolor: 'whitesmoke', width: 32, height: 32 }}>
              {canViewTeamList && (
                <GroupAddOutlinedIcon sx={{ width: 20, height: 20, color: '#303030' }} />
              )}
            </Avatar>
          </AvatarGroup>
        </Box>
      )}

      {/* Team Member Modal  */}
      <BootstrapDialog open={open} onClose={onClose} maxWidth="md">
        <DialogTitle>Manage Collaborators</DialogTitle>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
        <Divider />

        {isStudyLoading ? (
          <Spinner />
        ) : (
          <DialogContent>
            {/* <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                color: 'gray',
                borderRadius: 1,
                transition: 'background-color 0.2s',
                maxWidth: '500px',
              }}
            >
              <LinkIcon fontSize="small" sx={{ color: 'gray' }} />
              <Typography onClick={() => setOpenPublicShare(true)} variant="body2" color={'blue'}>
                Share this study, outside your team
              </Typography>
            </Box> */}
            <PublicShare
              key={`public-share-${studyId}-${type}`}
              study_link={survey?.study_link}
              open={openPublicShare}
              handleClose={() => setOpenPublicShare(false)}
              studyType={type === 'survey' ? 'Survey' : 'Interview'}
              studyId={studyId}
            />
            <Typography variant="body1" sx={{ mb: 2 }}>
              Share your study <b>{survey?.title ?? ''}</b> with your team members
            </Typography>
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {chips.map((chip, index) => (
                  <Box key={index}>
                    <Chip
                      label={chip}
                      onDelete={() => handleDeleteChip(chip)}
                      sx={{
                        pointerEvents: 'auto',
                        maxWidth: '500px',
                      }}
                      size="small"
                    />
                  </Box>
                ))}
              </Box>
            </Container>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'end',
                gap: 0.5,
                mb: 3,
              }}
            >
              <FormControl fullWidth>
                <Box display={'grid'} sx={{ mt: 1 }}>
                  <FormLabel>Invite members</FormLabel>
                  <Autocomplete
                    value={selectedEmail}
                    onChange={handleEmailChange}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    options={getEmailOptions(inputValue)}
                    freeSolo
                    disabled={!canInvite}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Enter email to invite collaborator"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    renderOption={({ key, ...props }, option) => (
                      <Box component="li" key={key} {...props}>
                        {option.startsWith('Add "') && option.endsWith('" to workspace') ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupAddOutlinedIcon fontSize="small" color="primary" />
                            <Typography variant="body2" color="primary">
                              {option}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupAddOutlinedIcon fontSize="small" color="primary" />
                            <Typography variant="body2" color="primary">
                              {option}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingRight: '9px',
                      },
                    }}
                  />
                </Box>
              </FormControl>
              <Box>
                <Select
                  disabled={!canInvite}
                  value={permission}
                  onChange={(e) => setPermission(e.target.value)}
                  sx={{ minWidth: 120 }}
                  size="small"
                >
                  <MenuItem value="view">Can view</MenuItem>
                  <MenuItem value="edit">Can edit</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  {isOwner && (
                    <MenuItem sx={{ color: 'error.main' }} value="owner">
                      Owner
                    </MenuItem>
                  )}
                </Select>
              </Box>

              <LoadingButton
                aria-label="Invite"
                variant="contained"
                type="button"
                color="primary"
                loading={isInvitePending}
                disabled={
                  isInvitePending ||
                  !canInvite ||
                  (!emailRegex.test(selectedEmail || inputValue.trim()) && !chips.length)
                }
                // size="large"
                onClick={handleTeamMemberInvite}
                sx={{ px: 4 }}
              >
                Invite
              </LoadingButton>
            </Box>
            {(teamMembers?.length > 0 || shareTeamMembers?.length > 0) && (
              <Box
                onClick={toggleShowTeamMembers}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  p: 1,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <AvatarGroup max={2}>
                    {[...new Set([...teamMembers, ...shareTeamMembers])]?.map((member, i) => {
                      if (!member?.user_email) return null;
                      const firstChar = member.user_email.charAt(0).toUpperCase();
                      const backgroundColor = getColorFromChar(firstChar);
                      return (
                        <Avatar key={i} sx={{ bgcolor: backgroundColor }}>
                          {firstChar}
                        </Avatar>
                      );
                    })}
                  </AvatarGroup>
                </Box>
                <Box sx={{ flex: 1, ml: 2 }}>
                  <Typography variant="subtitle2">View members</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage members access within this study
                  </Typography>
                </Box>
                {showTeamMembers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
            )}

            {showTeamMembers && (
              <>
                <List sx={{ px: 0 }}>
                  {teamMembers
                    ?.sort((a, b) => {
                      // Owner first
                      if (a.role === 'owner') return -1;
                      if (b.role === 'owner') return 1;
                      // Then admin
                      if (a.role === 'admin') return -1;
                      if (b.role === 'admin') return 1;
                      // Then regular members
                      return 0;
                    })
                    ?.map((member, i) => (
                      <ListItem key={i} sx={{ px: 1 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: getColorFromChar(member.user_email.charAt(0)) }}>
                            {member.user_email.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={member.user_name} secondary={member.user_email} />
                        {member.role === 'owner' ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 8 }}>
                            Owner
                          </Typography>
                        ) : (
                          <ListItemSecondaryAction>
                            <Select
                              value={
                                member.role === 'owner'
                                  ? 'owner'
                                  : member.role === 'admin'
                                    ? 'admin'
                                    : member.permissions.includes('edit')
                                      ? 'edit'
                                      : 'view'
                              }
                              size="small"
                              sx={{ minWidth: 120 }}
                            >
                              {
                                <MenuItem
                                  value="view"
                                  disabled={!canManageTeam}
                                  sx={{ justifyContent: 'space-between' }}
                                  onClick={() => {
                                    setDialogDescription({
                                      title: 'Confirm View Access',
                                      description: `Granting View access to ${member.user_email}`,
                                      okText: 'Grant Access',
                                    });
                                    show(() =>
                                      handleAddPermission(member.user_email, 'member', ['view']),
                                    );
                                  }}
                                >
                                  Can view
                                </MenuItem>
                              }
                              {
                                <MenuItem
                                  value="edit"
                                  disabled={!canManageTeam}
                                  sx={{ justifyContent: 'space-between' }}
                                  onClick={() => {
                                    setDialogDescription({
                                      title: 'Confirm Edit Access',
                                      description: `Granting Edit access to ${member.user_email}`,
                                      okText: 'Grant Access',
                                    });
                                    show(() =>
                                      handleAddPermission(member.user_email, 'member', [
                                        'view',
                                        'edit',
                                      ]),
                                    );
                                  }}
                                >
                                  Can edit
                                </MenuItem>
                              }
                              {canManageTeam && (
                                <MenuItem
                                  selected={member.permissions.includes('admin')}
                                  disabled={member.role === 'admin'}
                                  value="admin"
                                  onClick={() => {
                                    setDialogDescription({
                                      title: 'Confirm Admin Access',
                                      description: `Granting Admin access to ${member.user_email}`,
                                      okText: 'Grant Access',
                                    });
                                    show(() =>
                                      handleAddPermission(member.user_email, 'admin', [
                                        'view',
                                        'edit',
                                        'admin',
                                      ]),
                                    );
                                  }}
                                >
                                  Admin
                                </MenuItem>
                              )}
                              <Divider />
                              {isOwner && (
                                <MenuItem
                                  sx={{ justifyContent: 'space-between', color: 'error.main' }}
                                  disabled={!isOwner}
                                  value="owner"
                                  onClick={() => {
                                    setDialogDescription({
                                      title: 'Confirm Owner Access',
                                      description: `Granting Owner access to ${member.user_email}`,
                                      okText: 'Grant Access',
                                    });
                                    show(() => handleChangeOwner(member.user_email));
                                  }}
                                >
                                  Owner
                                </MenuItem>
                              )}
                              {!isOwner && member?.user_email === userData?.email && (
                                <MenuItem
                                  value="leave"
                                  sx={{ color: 'error.main' }}
                                  onClick={() => {
                                    setDialogDescription({
                                      title: 'Leave Study',
                                      description: `You won't be able to access this study if you leave it.`,
                                      okText: 'Leave',
                                    });
                                    show(() => handleRemoveMember(member.user_email));
                                  }}
                                >
                                  Leave
                                </MenuItem>
                              )}
                              {(isOwner ||
                                (isAdmin &&
                                  member?.user_email !== userData.email &&
                                  member.role !== 'admin')) && (
                                <MenuItem
                                  onClick={() => {
                                    setDialogDescription({
                                      title: 'Confirm Remove Member',
                                      description: `${member.user_email} won't be able to access this study if you remove them.`,
                                      okText: 'Remove Member',
                                    });
                                    show(() => handleRemoveMember(member.user_email));
                                  }}
                                  value="remove"
                                  sx={{ color: 'error.main' }}
                                >
                                  Remove
                                </MenuItem>
                              )}
                            </Select>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    ))}
                  {canManageTeam &&
                    shareTeamMembers?.length > 0 &&
                    shareTeamMembers?.some((member) => !member?.is_link_based) && <Divider />}

                  {canManageTeam &&
                    !isTeamLoading &&
                    shareTeamMembers?.map((member, i) => {
                      if (member?.is_link_based || !member?.user_email) return null;
                      return (
                        <ListItem key={i} sx={{ px: 1 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: getColorFromChar(member.user_email.charAt(0)) }}>
                              {member?.user_email.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={member?.user_email} secondary={'invite sent'} />
                          <ListItemSecondaryAction>
                            <Select
                              value={
                                member.role === 'owner'
                                  ? 'owner'
                                  : member.role === 'admin'
                                    ? 'admin'
                                    : member.permissions.includes('edit')
                                      ? 'edit'
                                      : 'view'
                              }
                              size="small"
                              sx={{ minWidth: 120 }}
                            >
                              {member.permissions.includes('view') &&
                                member.role !== 'admin' &&
                                member.role !== 'owner' &&
                                !member.permissions.includes('edit') && (
                                  <MenuItem disabled value="view">
                                    Can view
                                  </MenuItem>
                                )}
                              {member.permissions.includes('edit') &&
                                member.role !== 'admin' &&
                                member.role !== 'owner' && (
                                  <MenuItem disabled value="edit">
                                    Can edit
                                  </MenuItem>
                                )}
                              {member.role === 'admin' && (
                                <MenuItem disabled value="admin">
                                  Admin
                                </MenuItem>
                              )}
                              {member.role === 'owner' && (
                                <MenuItem disabled value="owner">
                                  Owner
                                </MenuItem>
                              )}
                              {(isOwner || (isAdmin && member.role !== 'owner')) && (
                                <MenuItem
                                  onClick={async () => {
                                    setDialogDescription({
                                      title: 'Remove Share Request',
                                      description: `Are you sure you want to remove share request for ${member.user_email}`,
                                      okText: 'Remove',
                                    });
                                    show(() => handleDeleteShareRequest(member.user_email));
                                  }}
                                  value="remove"
                                  sx={{ color: 'error.main' }}
                                >
                                  Remove
                                </MenuItem>
                              )}
                            </Select>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                </List>
              </>
            )}
          </DialogContent>
        )}
      </BootstrapDialog>

      {/* Delete confirmation dialog */}
      <DeleteConfirmationDialog
        open={dialogOpen}
        onClose={handleClose}
        isPending={isDeletePending}
        onConfirm={handleConfirm}
        title={dialogDescription.title}
        okText={dialogDescription.okText}
        content={
          <Box>
            <Typography>{dialogDescription.description}</Typography>
          </Box>
        }
      />

      <InviteMemberDialogCollaborators
        open={openInviteDialog}
        prefilledEmail={inviteEmail}
        onClose={() => {
          setOpenInviteDialog(false);
          setInviteEmail('');
        }}
        onSuccess={handleInviteDialogSuccess}
      />
    </>
  );
}
